import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { BookCheck } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { icons } from "../../assets";
import AppLoader from "../../components/appLoader";
import TaskSummaryCard from "../../components/card/taskSummaryCard";
import CustomButton from "../../components/customButton";
import PaginatedTable from "../../components/dynamicTable";
import ViewWorkersDialog from "./viewWorkersDialog";
import { openSnackAlert } from "../../helper";
import {
  canDownloadTaskAttachments,
  downloadTaskCompletedAttachmentsPdf,
  isLandscapeTask,
} from "../../utils/taskPdfTemplate";
import {
  createTask,
  getAllTasks,
  getSubmittedImages,
  getTaskStats,
  taskDelete,
} from "../../services/TaskManagement";
import ConfirmationDialog from "../../components/confirmationDialog";
import ViewSubmittedImagesDialog from "./viewSubmittedImagesDialog";
import useUserStore from "../../zustand/useUserStore";

const tableHeaders = [
  { title: "Task Name", value: "name", align: "center" },
  // { title: "Due Date", value: "taskDueDate", align: "center" },
  { title: "Start Date", value: "startDate", align: "center" },
  { title: "End Date", value: "endDate", align: "center" },
  { title: "Progress", value: "status", align: "center" },
  // { title: "Attachments", value: "attachments", align: "center" },
  // { title: "Assigns", value: "viewAssigns", align: "center" },
  {
    title: "Completed Attachments",
    value: "completedAttachments",
    align: "center",
  },
  { title: "Worker Description", value: "workerDescription", align: "center" }, 
  {
    title: "Task Type",
    value: "taskType",
    align: "center",
  },
  { title: "Reschedule", value: "reschedule", align: "center" },
  { title: "View Submitted Images", value: "ViewSubmittedImages", align: "center" },
  { title: "Subtasks", value: "viewSubtasks", align: "center" },
  { title: "Priority", value: "priority", align: "center" },
  { title: "Actions", value: "actions", align: "center" },
];

const displayRows = tableHeaders.map((header) => header.value);

const TaskManagement = () => {
  const [allTasks, setAllTasks] = useState([]);
  const [taskStats, setTaskStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [workersDialogOpen, setWorkersDialogOpen] = useState(false);
  const [selectedTaskWorkers, setSelectedTaskWorkers] = useState([]);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [selectedTaskToDelete, setSelectedTaskToDelete] = useState(null);
  const [submittedImagesDialogOpen, setSubmittedImagesDialogOpen] = useState(false);
  const [selectedSubmittedTask, setSelectedSubmittedTask] = useState(null);
  const [selectedSubmittedWorkerId, setSelectedSubmittedWorkerId] = useState("");
  const [submittedWorkerData, setSubmittedWorkerData] = useState(null);
  const [submittedWorkerLoading, setSubmittedWorkerLoading] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [selectedDownloadTask, setSelectedDownloadTask] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [selectedRescheduleTask, setSelectedRescheduleTask] = useState(null);
  const [rescheduleDates, setRescheduleDates] = useState({ startDate: "", endDate: "" });
  const { user } = useUserStore();
  const navigate = useNavigate();

  useEffect(() => {
    handleGetAllTasks();
  }, []);

  const handleGetAllTasks = async () => {
    try {
      setLoading(true);
      const response = await getAllTasks();
      const taskStatsResponse = await getTaskStats();
      const taskResponse = response?.data;
      setLoading(false);
      if (taskResponse?.status === "success") {
        setAllTasks(taskResponse.data);
      } else {
        openSnackAlert(taskResponse?.message || "Get tasks failed", "error");
      }
      if (
        taskStatsResponse?.status === 200 ||
        taskStatsResponse?.status === 201
      ) {
        setTaskStats(taskStatsResponse.data.data);
      } else {
        openSnackAlert(
          taskStatsResponse?.message || "Get task stats failed",
          "error"
        );
      }
    } catch (error) {
      setLoading(false);
      openSnackAlert(error?.message || "Something went wrong", "error");
    }
  };

  const handleEditTask = (row) => {
    navigate(`/edit-task/${row._id}`, {
      state: {
        taskData: row,
      },
    });
  };

  const downloadCompletedAttachmentsCsv = (row) => {
    try {
      if (!canDownloadTaskAttachments(row)) {
        openSnackAlert("No completed attachments found for this task", "warning");
        return;
      }

      const toCsvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

      const normalizeUrlList = (items) =>
        (items || [])
          .map((item) => (typeof item === "string" ? item : item?.url))
          .filter(Boolean)
          .join(" | ");

      const commentTextList = (comments) =>
        (comments || [])
          .map((c) => String(c?.text ?? "").trim())
          .filter(Boolean)
          .join(" | ");

      let csvHeaders;
      let csvRows;

      if (isLandscapeTask(row)) {
        const img = row.image || {};
        csvHeaders = [
          "Task Name",
          "Description",
          "Worker Description",
          "Start Date",
          "End Date",
          "Status",
          "Priority",
          "Before Images",
          "After Images",
          "Before Comments",
          "After Comments",
        ];
        csvRows = [
          [
            toCsvCell(row.name || ""),
            toCsvCell(row.description || ""),
            toCsvCell(row.workerDescription || ""),
            toCsvCell(row.startDate || ""),
            toCsvCell(row.endDate || ""),
            toCsvCell(row.status || ""),
            toCsvCell(row.priority || ""),
            toCsvCell(normalizeUrlList(img.beforeWorkImages)),
            toCsvCell(normalizeUrlList(img.afterWorkImages)),
            toCsvCell(commentTextList(row.beforeComment)),
            toCsvCell(commentTextList(row.afterComment)),
          ],
        ];
      } else {
        const normalizeList = (value) => {
          if (!value) return [];
          return Array.isArray(value) ? value.filter(Boolean) : [value];
        };

        const workerNameFromSubtask = (subtask) => {
          if (subtask?.worker) {
            return `${subtask.worker.firstName || ""} ${subtask.worker.lastName || ""}`.trim();
          }
          return subtask?.employeeName || "";
        };

        csvHeaders = [
          "Location Name",
          "Location Address",
          "Location Number",
          "Instructions",
          "Time In",
          "Time Out",
          "Before Images",
          "After Images",
          "Receipt",
          "Comments",
          "Worker Name",
          "Worker Email",
          "Completed Status",
        ];

        csvRows = row.subTask.map((subtask) => [
          toCsvCell(subtask.locationName || ""),
          toCsvCell(subtask.locationAddress || ""),
          toCsvCell(subtask.locationNumber || ""),
          toCsvCell(subtask.instructions || ""),
          toCsvCell(subtask.timeIn || ""),
          toCsvCell(subtask.timeOut || ""),
          toCsvCell(normalizeList(subtask.beforeImage).join(" | ")),
          toCsvCell(normalizeList(subtask.afterImage).join(" | ")),
          toCsvCell(subtask.receipt || ""),
          toCsvCell(normalizeList(subtask.comments).join(" | ")),
          toCsvCell(workerNameFromSubtask(subtask)),
          toCsvCell(subtask?.worker?.email || ""),
          toCsvCell(subtask.completed ? "Yes" : "No"),
        ]);
      }

      const csvContent = [
        csvHeaders.join(","),
        ...csvRows.map((item) => item.join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `${isLandscapeTask(row) ? "landscape" : "completed_attachments"}_${row.name || "task"}_${
          new Date().toISOString().split("T")[0]
        }.csv`
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      openSnackAlert("Excel file downloaded successfully", "success");
    } catch (error) {
      console.error("Error downloading CSV:", error);
      openSnackAlert("Error downloading Excel file", "error");
    }
  };

  const downloadCompletedAttachmentsPdf = async (row) => {
    try {
      const result = await downloadTaskCompletedAttachmentsPdf(row);
      if (!result.ok) {
        openSnackAlert(result.message, "warning");
        return;
      }
      openSnackAlert("PDF file downloaded successfully", "success");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      openSnackAlert("Error downloading PDF file", "error");
    }
  };

  const handleDownloadCompletedAttachments = (row) => {
    if (!canDownloadTaskAttachments(row)) {
      openSnackAlert("No completed attachments found for this task", "warning");
      return;
    }
    setSelectedDownloadTask(row);
    setDownloadDialogOpen(true);
  };

  const handleCloseDownloadDialog = () => {
    setDownloadDialogOpen(false);
    setSelectedDownloadTask(null);
  };

  const handleDownloadByType = async (type) => {
    if (!selectedDownloadTask) return;

    if (type === "excel") {
      downloadCompletedAttachmentsCsv(selectedDownloadTask);
    } else {
      await downloadCompletedAttachmentsPdf(selectedDownloadTask);
    }
    handleCloseDownloadDialog();
  };

  const summaryCards = [
    {
      icon: icons.taskIcon,
      label: "Total Task",
      value: taskStats?.totalJobs || 0,
    },
    {
      icon: icons.completedIcon,
      label: "Completed",
      value: taskStats?.completedJobs || 0,
    },
    {
      icon: icons.pendingIcon,
      label: "Pending",
      value: taskStats?.pendingJobs || 0,
    },
  ];

  const handleViewAssigns = (row) => {
    console.log(row, "row");
    // Assuming workers data is in row.worker or row.assignedWorkers
    const workers = row.worker || row.assignedWorkers || [];
    setSelectedTaskWorkers(workers);
    setWorkersDialogOpen(true);
  };


  const handleDeleteTaskClick = (row) => {
    setSelectedTaskToDelete(row);
    setConfirmationDialogOpen(true);
  };

  const handleConfirmDeleteTask = async () => {
    if (!selectedTaskToDelete?._id) {
      setConfirmationDialogOpen(false);
      return;
    }
  
    try {
      setLoading(true);
      const response = await taskDelete(selectedTaskToDelete._id);
  
      if (response?.status === 200 || response?.status === 201) {
        openSnackAlert("Task deleted successfully", "success");
        handleGetAllTasks();
      } else {
        openSnackAlert(response?.message || "Task deletion failed", "error");
      }
    } catch (error) {
      openSnackAlert(error?.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
      setConfirmationDialogOpen(false);
      setSelectedTaskToDelete(null);
    }
  };

  const handleViewSubmittedImages = (row) => {
    const workers = row?.worker || [];
    if (!workers.length) {
      openSnackAlert("No workers found for this task", "warning");
      return;
    }

    setSelectedSubmittedTask(row);
    setSelectedSubmittedWorkerId("");
    setSubmittedWorkerData(null);
    setSubmittedImagesDialogOpen(true);
  };

  const handleViewSubtasks = (row) => {
    navigate(`/jobs/${row._id}/subtasks`, {
      state: {
        jobName: row?.name || row?.title || "",
      },
    });
  };

  const paginatedTasks = useMemo(() => {
    const startIndex = currentPage * rowsPerPage;
    return allTasks.slice(startIndex, startIndex + rowsPerPage);
  }, [allTasks, currentPage, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const handleWorkerSubmittedImages = async (worker) => {
    if (!selectedSubmittedTask?._id || !worker?._id) return;

    try {
      setSubmittedWorkerLoading(true);
      setSelectedSubmittedWorkerId(worker._id);
      const response = await getSubmittedImages(selectedSubmittedTask._id, worker._id);
      const apiResponse = response?.data;

      if (apiResponse?.status === "success") {
        setSubmittedWorkerData(apiResponse?.data || null);
      } else {
        setSubmittedWorkerData(null);
        openSnackAlert(
          apiResponse?.message || "Failed to fetch submitted images",
          "error"
        );
      }
    } catch (error) {
      setSubmittedWorkerData(null);
      openSnackAlert(error?.message || "Something went wrong", "error");
    } finally {
      setSubmittedWorkerLoading(false);
    }
  };

  const handleOpenRescheduleDialog = (row) => {
    const minDate = dayjs().format("YYYY-MM-DD");
    setSelectedRescheduleTask(row);
    setRescheduleDates({
      startDate: minDate,
      endDate: minDate,
    });
    setRescheduleDialogOpen(true);
  };

  const handleCloseRescheduleDialog = () => {
    setRescheduleDialogOpen(false);
    setSelectedRescheduleTask(null);
    setRescheduleDates({ startDate: "", endDate: "" });
  };

  const handleRescheduleDateChange = (field, value) => {
    setRescheduleDates((prev) => ({ ...prev, [field]: value }));
  };

  const handleConfirmReschedule = async () => {
    if (!selectedRescheduleTask?._id) return;

    const { startDate, endDate } = rescheduleDates;
    const today = dayjs().startOf("day");

    if (!startDate || !endDate) {
      openSnackAlert("Start date and end date are required", "error");
      return;
    }

    const start = dayjs(startDate).startOf("day");
    const end = dayjs(endDate).startOf("day");

    if (start.isBefore(today)) {
      openSnackAlert("Start date must be today or a future date", "error");
      return;
    }

    if (end.isBefore(start)) {
      openSnackAlert("End date must be same or after start date", "error");
      return;
    }

    const existingWorkers = Array.isArray(selectedRescheduleTask.worker)
      ? selectedRescheduleTask.worker
      : selectedRescheduleTask.worker
      ? [selectedRescheduleTask.worker]
      : [];

    const workerIds = existingWorkers
      .map((workerItem) => workerItem?._id || workerItem)
      .filter(Boolean);

    if (!workerIds.length) {
      openSnackAlert("No assigned workers found for this task", "warning");
      return;
    }

    const sanitizedSubTasks = (selectedRescheduleTask.subTask || []).map((item) => {
      const normalizedWorker = item?.worker?._id || item?.worker || "";
      return {
        ...item,
        ...(normalizedWorker ? { worker: String(normalizedWorker) } : {}),
      };
    });

    const payload = {
      worker: workerIds,
      type: selectedRescheduleTask.type || "task",
      assignedBy: user?._id || selectedRescheduleTask?.assignedBy?._id,
      name: selectedRescheduleTask.name || "Rescheduled Task",
      priority: selectedRescheduleTask.priority || "medium",
      startDate,
      endDate,
      description: selectedRescheduleTask.description || "",
      subTask: sanitizedSubTasks,
      taskImages: selectedRescheduleTask.taskImages || [],

      isNotifiedEmail:
        selectedRescheduleTask.isNotifiedEmail !== undefined
          ? selectedRescheduleTask.isNotifiedEmail
          : true,
      isPushNotified:
        selectedRescheduleTask.isPushNotified !== undefined
          ? selectedRescheduleTask.isPushNotified
          : true,
    };

    if (selectedRescheduleTask.attachment) {
      payload.attachment = selectedRescheduleTask.attachment;
    }

    if (!payload.assignedBy) {
      openSnackAlert("Assigned user not found. Please login again.", "error");
      return;
    }

    try {
      setLoading(true);
      const response = await createTask(payload);
      const resData = response?.data;

      if (resData?.status === "success") {
        openSnackAlert("Task rescheduled successfully", "success");
        handleCloseRescheduleDialog();
        handleGetAllTasks();
      } else {
        openSnackAlert(resData?.message || "Failed to reschedule task", "error");
      }
    } catch (error) {
      openSnackAlert(error?.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      bgcolor="primary.bgLight"
      padding={"20px"}
      borderRadius={"22px"}
      mt={0}
    >
      <AppLoader isVisible={loading} />
      <Grid container spacing={2}>
        <Grid item sm={12} md={5}>
          <Typography variant="h4">Task Management</Typography>
          <Typography variant="description">
            Assign and track all form of employees duties
          </Typography>
        </Grid>

        <Grid item sm={12} md={7}>
          <Box display={"flex"} justifyContent={"flex-end"} gap={1.5}>
            <CustomButton
              btnLabel={"Assign Task"}
              variant={"webbutton"}
              btnTextSize={10}
              startIcon={<BookCheck size={14} strokeWidth={2} />}
              handlePressBtn={() => {
                navigate("/add-task");
              }}
            />
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={2}>
        {summaryCards.map((card, idx) => (
          <Grid item xs={12} sm={6} md={4} key={idx}>
            <TaskSummaryCard
              icon={card.icon}
              label={card.label}
              value={card.value}
            />
          </Grid>
        ))}
      </Grid>

      <Box mt={1}>
        <PaginatedTable
          tableHeader={tableHeaders}
          tableData={paginatedTasks}
          displayRows={displayRows}
          showPagination={true}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          totalRecords={allTasks.length}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          onEdit={handleEditTask}
          handleDownloadCompletedAttachments={
            handleDownloadCompletedAttachments
          }
          onDelete={handleDeleteTaskClick}
          handleViewAssigns={handleViewAssigns}
          handleViewSubmittedImages={handleViewSubmittedImages}
          handleViewSubtasks={handleViewSubtasks}
          handleRescheduleTask={handleOpenRescheduleDialog}
        />
      </Box>

      <ViewWorkersDialog
        open={workersDialogOpen}
        onClose={() => setWorkersDialogOpen(false)}
        workers={selectedTaskWorkers}
      />
      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => {
          setConfirmationDialogOpen(false);
          setSelectedTaskToDelete(null);
        }}
        onConfirm={handleConfirmDeleteTask}
      />
      <ViewSubmittedImagesDialog
        open={submittedImagesDialogOpen}
        onClose={() => {
          setSubmittedImagesDialogOpen(false);
          setSelectedSubmittedTask(null);
          setSelectedSubmittedWorkerId("");
          setSubmittedWorkerData(null);
          setSubmittedWorkerLoading(false);
        }}
        workers={selectedSubmittedTask?.worker || []}
        selectedWorkerId={selectedSubmittedWorkerId}
        submittedData={submittedWorkerData}
        loading={submittedWorkerLoading}
        onWorkerClick={handleWorkerSubmittedImages}
      />
      <Dialog
        open={downloadDialogOpen}
        onClose={handleCloseDownloadDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "22px",
            padding: "20px",
          },
        }}
      >
        <DialogTitle>Select Download Format</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Which format do you want to download for completed attachments?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Box display={"flex"} gap={1} justifyContent={"space-between"} width={"100%"} px={2} py={1} >
          <CustomButton handlePressBtn={handleCloseDownloadDialog} btnLabel={"Cancel"} btnBgColor="#fff" btnTextColor="#225B27" borderColor="#225B27" isBorder="1px solid #225B27" width={100} sx={{ boxShadow: 0, borderRadius: "22px" }} />


<Box display={"flex"} gap={1} justifyContent={"flex-end"} width={"100%"}>
         <CustomButton
          handlePressBtn={() => {
            handleDownloadByType("excel");
          }}
          btnLabel={"Excel"}
          btnBgColor="#fff"
          btnTextColor="#225B27"
          borderColor="#225B27"
          isBorder="1px solid #225B27"
          width={100}
          sx={{ boxShadow: 0, borderRadius: "22px" }}
        />
          <CustomButton
          handlePressBtn={() => {
            handleDownloadByType("pdf");
          }}
          btnLabel={"PDF"}
          btnBgColor="#225B27"
          btnTextColor="#fff"
          borderColor="#225B27"
          width={100}
          sx={{ boxShadow: 0, borderRadius: "22px" }}
        />
</Box>
      

          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rescheduleDialogOpen}
        onClose={handleCloseRescheduleDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "22px",
            padding: "20px",
          },
        }}
      >
        <DialogTitle>Reschedule Task</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={2}>
            Select new start and end date to assign this task again for upcoming schedule.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Start Date"
                type="date"
                value={rescheduleDates.startDate}
                onChange={(e) => handleRescheduleDateChange("startDate", e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: dayjs().format("YYYY-MM-DD") }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="End Date"
                type="date"
                value={rescheduleDates.endDate}
                onChange={(e) => handleRescheduleDateChange("endDate", e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: rescheduleDates.startDate || dayjs().format("YYYY-MM-DD"),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Box
            display={"flex"}
            gap={1}
            justifyContent={"space-between"}
            width={"100%"}
            px={2}
            py={1}
          >
            <CustomButton
              handlePressBtn={handleCloseRescheduleDialog}
              btnLabel={"Cancel"}
              btnBgColor="#fff"
              btnTextColor="#225B27"
              borderColor="#225B27"
              isBorder="1px solid #225B27"
              width={120}
              sx={{ boxShadow: 0, borderRadius: "22px" }}
            />
            <CustomButton
              handlePressBtn={handleConfirmReschedule}
              btnLabel={"Reschedule"}
              btnBgColor="#225B27"
              btnTextColor="#fff"
              borderColor="#225B27"
              width={120}
              sx={{ boxShadow: 0, borderRadius: "22px" }}
            />
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskManagement;
